import { zodResolver } from '@/utils/zodResolver'
import { useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { useNavigate, useSearchParams } from 'react-router'
import { z } from 'zod'

import { AuthLayout } from '@ankor-io/ui-components/src/AuthLayout'
import { Button } from '@ankor-io/ui-components/src/Button'
import { ErrorMessage, Field, Label } from '@ankor-io/ui-components/src/Fieldset'
import { Heading } from '@ankor-io/ui-components/src/Heading'
import { Input } from '@ankor-io/ui-components/src/Input'
import { Text } from '@ankor-io/ui-components/src/Text'

const formSchema = z.object({
  email: z.string().email({
    message: 'Invalid email address.',
  }),
  firstName: z
    .string()
    .min(1, {
      message: 'First name is required.',
    })
    .max(50, {
      message: 'First name must be less than 50 characters.',
    })
    .regex(/^[a-zA-Z\s\-'.]+$/, {
      message: 'First name can only contain letters, spaces, hyphens, apostrophes, and periods.',
    })
    .refine((val) => val.trim().length >= 1, {
      message: 'First name cannot be empty.',
    }),
  lastName: z
    .string()
    .min(1, {
      message: 'Last name is required.',
    })
    .max(50, {
      message: 'Last name must be less than 50 characters.',
    })
    .regex(/^[a-zA-Z\s\-'.]+$/, {
      message: 'Last name can only contain letters, spaces, hyphens, apostrophes, and periods.',
    })
    .refine((val) => val.trim().length >= 1, {
      message: 'Last name cannot be empty.',
    }),
})

const SignUpPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email')
  // Extract inviteId from URL to support invite acceptance flow
  // This is so we can hide the default org when a new user creates an account as part of the invite acceptance flow.
  const inviteId = searchParams.get('inviteId')

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: email || '',
      firstName: '',
      lastName: '',
    },
  })

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    const createAccountCall = fetch('/api/iam/account', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        inviteId: inviteId, // Include inviteId to hide default org during invite acceptance
      }),
    }).then(async (response) => {
      const data = await response.json()

      if (!response.ok) {
        if (response.status === 409) {
          throw new Error('Account already exists. Please sign in.')
        } else {
          throw new Error('Something went wrong. Please try again or contact support.')
        }
      }

      return data
    })

    toast.promise(createAccountCall, {
      loading: 'Creating account...',
      success: () => {
        const urlParams = new URLSearchParams(window.location.search)
        const inviteId = urlParams.get('inviteId')
        if (inviteId) {
          navigate(`/invite/${inviteId}`, { replace: true })
        } else {
          navigate('/sign-in', { replace: true })
        }
        return 'Account created successfully. Please sign in.'
      },
      error: (err) => err.message,
    })
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-md space-y-6">
        <div className="space-y-2 text-center">
          <Heading level={1}>Create Account</Heading>
          <Text>Enter your details to create a new account</Text>
        </div>

        <form
          role="form"
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-4"
        >
          <Field>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="john@example.com"
              {...form.register('email')}
            />
            {form.formState.errors.email && <ErrorMessage>{form.formState.errors.email.message}</ErrorMessage>}
          </Field>

          <Field>
            <Label htmlFor="firstName">First Name</Label>
            <Input
              id="firstName"
              type="text"
              placeholder="John"
              {...form.register('firstName')}
            />
            {form.formState.errors.firstName && <ErrorMessage>{form.formState.errors.firstName.message}</ErrorMessage>}
          </Field>

          <Field>
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              id="lastName"
              type="text"
              placeholder="Doe"
              {...form.register('lastName')}
            />
            {form.formState.errors.lastName && <ErrorMessage>{form.formState.errors.lastName.message}</ErrorMessage>}
          </Field>

          <Button
            type="submit"
            className="w-full"
            disabled={form.formState.isSubmitting}
            color="primary"
          >
            Create Account
          </Button>
        </form>

        <div className="text-center">
          <Button
            plain
            disabled={form.formState.isSubmitting}
            to={`/sign-in${form.watch('email') ? `?email=${encodeURIComponent(form.watch('email'))}` : ''}`}
          >
            Already have an account? Sign in
          </Button>
        </div>
      </div>
    </AuthLayout>
  )
}

export default SignUpPage
